import { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { LogIn } from 'lucide-react';
import { z } from 'zod';

import { ApiError, apiClient, toNetworkError } from '@/api';
import { authSession } from '@/lib/auth-session';
import { getLoginReturnPath } from '@/lib/auth-redirect';
import { cn } from '@/lib/utils';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Field, FieldDescription, FieldGroup, FieldLabel } from '@/components/ui/field';
import { Input } from '@/components/ui/input';
import { Spinner } from '@/components/ui/spinner';

const loginSchema = z.object({
  username: z.string().trim().min(1, 'Введите логин'),
  password: z.string().trim().min(1, 'Введите пароль')
});

export type LoginFormValues = z.infer<typeof loginSchema>;

export function LoginForm({ className, ...props }: React.ComponentProps<'div'>) {
  const navigate = useNavigate();
  const location = useLocation();
  const [isLoading, setIsLoading] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const form = useForm<LoginFormValues>({
    resolver: zodResolver(loginSchema),
    defaultValues: {
      username: '',
      password: ''
    }
  });

  const handleSubmit = form.handleSubmit(async (values) => {
    setSubmitError(null);
    setIsLoading(true);

    try {
      const json = await apiClient.login(values);

      authSession.setUsername(json.username);
      navigate(getLoginReturnPath(location.state), { replace: true });
    } catch (error) {
      const normalizedError = toNetworkError(error);
      setSubmitError(
        normalizedError instanceof ApiError
          ? normalizedError.message
          : (normalizedError as Error).message || 'Не удалось выполнить вход'
      );
    } finally {
      setIsLoading(false);
    }
  });

  return (
    <div className={cn('flex flex-col gap-6', className)} {...props}>
      <Card className="overflow-hidden p-0">
        <CardContent className="grid p-0 md:grid-cols-2">
          <form className="p-6 md:p-8" onSubmit={(event) => void handleSubmit(event)}>
            <FieldGroup>
              <div className="flex flex-col items-center gap-2 text-center">
                <h1 className="text-2xl font-bold">Добро пожаловать</h1>
                <p className="text-muted-foreground text-balance">Войдите в свою учетную запись</p>
              </div>
              <Field>
                <FieldLabel htmlFor="username">Логин</FieldLabel>
                <Input
                  id="username"
                  autoComplete="username"
                  placeholder="Введите логин"
                  {...form.register('username')}
                />
                {form.formState.errors.username ? (
                  <p className="text-destructive text-sm">{form.formState.errors.username.message}</p>
                ) : null}
              </Field>
              <Field>
                <div className="flex items-center">
                  <FieldLabel htmlFor="password">Пароль</FieldLabel>
                  <a href="#" className="ml-auto text-sm underline-offset-2 hover:underline">
                    Забыли пароль?
                  </a>
                </div>
                <Input
                  id="password"
                  type="password"
                  autoComplete="current-password"
                  placeholder="Введите пароль"
                  {...form.register('password')}
                />
                {form.formState.errors.password ? (
                  <p className="text-destructive text-sm">{form.formState.errors.password.message}</p>
                ) : null}
              </Field>
              {submitError ? (
                <Alert variant="destructive">
                  <AlertTitle>Ошибка входа</AlertTitle>
                  <AlertDescription>{submitError}</AlertDescription>
                </Alert>
              ) : null}
              <Field>
                <Button type="submit" disabled={isLoading}>
                  {isLoading ? (
                    <span className="inline-flex items-center gap-2">
                      <Spinner className="size-4" />
                      Вход...
                    </span>
                  ) : (
                    <>
                      <LogIn className="size-4" />
                      Войти
                    </>
                  )}
                </Button>
              </Field>
            </FieldGroup>
          </form>
          <div className="bg-muted relative hidden md:block">
            <img
              src="/placeholder.svg"
              alt="Image"
              className="absolute inset-0 h-full w-full object-cover dark:brightness-[0.2] dark:grayscale"
            />
          </div>
        </CardContent>
      </Card>
      <FieldDescription className="px-6 text-center">
        Нажимая продолжить, вы принимаете <a href="#">условия сервиса</a> и{' '}
        <a href="#">политику конфиденциальности</a>.
      </FieldDescription>
    </div>
  );
}
